/**
 * EbookReader — leitor de produto digital embutido.
 * Requer login; verifica identidade antes de gerar URL assinada.
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiDownload, FiAlertCircle } from 'react-icons/fi';
import api from '../services/api';

const EbookReader: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [produtoNome, setProdutoNome] = useState('');
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [iframeError, setIframeError] = useState(false);

  useEffect(() => {
    if (!id) return;

    const load = async () => {
      try {
        // Carregar info do acesso
        const infoRes = await api.get(`/client/digital/my-accesses/${id}`);
        if (infoRes.data?.success) {
          setProdutoNome(infoRes.data.data?.produtoNome || 'Ebook');
        }

        // Obter URL assinada para visualização (view = inline)
        const viewRes = await api.get(`/client/digital/view/${id}`);
        if (viewRes.data?.success && viewRes.data?.data?.url) {
          setPdfUrl(viewRes.data.data.url);
        } else {
          setError('Não foi possível carregar o documento.');
        }

        // Obter URL para download (para o botão de guardar)
        const dlRes = await api.get(`/client/digital/download/${id}`);
        if (dlRes.data?.success && dlRes.data?.data?.url) {
          setDownloadUrl(dlRes.data.data.url);
        }
      } catch (err: any) {
        const msg = err?.response?.data?.error || err.message || 'Erro ao carregar o documento.';
        setError(msg);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id]);

  return (
    <div className="flex flex-col h-screen bg-gray-900">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-800 text-white flex-shrink-0">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
        >
          <FiArrowLeft />
          <span className="text-sm">Voltar</span>
        </button>

        <h1 className="text-sm font-semibold truncate max-w-[50vw] text-center">
          {produtoNome}
        </h1>

        {downloadUrl ? (
          <a
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-gray-300 hover:text-white transition-colors text-sm"
          >
            <FiDownload />
            <span className="hidden sm:inline">Guardar</span>
          </a>
        ) : (
          <div className="w-16" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 relative">
        {loading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900 text-white gap-3">
            <div className="w-10 h-10 rounded-full border-2 border-white/20 border-t-white animate-spin" />
            <p className="text-sm text-gray-400">A preparar o documento...</p>
          </div>
        )}

        {error && !loading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900 text-white gap-4 px-8 text-center">
            <FiAlertCircle className="text-4xl text-red-400" />
            <p className="text-sm text-gray-300">{error}</p>
            <button
              onClick={() => navigate('/meus-downloads')}
              className="px-4 py-2 bg-pagali-purple text-white rounded-lg text-sm"
            >
              Ir para Meus Downloads
            </button>
          </div>
        )}

        {pdfUrl && !loading && !iframeError && (
          <iframe
            src={pdfUrl}
            title={produtoNome}
            className="w-full h-full border-0"
            allow="fullscreen"
            onError={() => setIframeError(true)}
            onLoad={(e) => {
              // Detectar iframe em branco: contentDocument acessível mas vazio (same-origin fallback)
              try {
                const doc = (e.target as HTMLIFrameElement).contentDocument;
                if (doc && doc.body && doc.body.innerHTML === '') setIframeError(true);
              } catch { /* cross-origin — normal, ignorar */ }
            }}
          />
        )}

        {pdfUrl && !loading && iframeError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900 text-white gap-4 px-8 text-center">
            <FiAlertCircle className="text-4xl text-yellow-400" />
            <p className="text-sm text-gray-300">
              O documento não pôde ser visualizado aqui.<br />
              Usa o botão abaixo para abrir numa nova janela.
            </p>
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-5 py-2.5 bg-pagali-purple text-white rounded-lg text-sm font-medium"
            >
              Abrir em nova janela
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default EbookReader;
